import { useCallback, useEffect, useRef, useState } from 'react';
import { apiRequest } from '../utils/api';

const mapOutletToOption = (outlet) => ({
  value: String(outlet.id ?? ''),
  label:
    outlet.branchName ||
    outlet.name ||
    outlet.showId ||
    (outlet.id != null ? `Outlet #${outlet.id}` : 'Outlet'),
});

const useOutletSearch = (config = {}) => {
  const { navigate, companyId, minimumQueryLength = 2, enabled = true } =
    typeof config === 'object' && config !== null && !Array.isArray(config)
      ? config
      : { companyId: config };
  const [searchTerm, setSearchTerm] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const pendingRequestId = useRef(0);
  const debounceRef = useRef(null);

  const search = useCallback(
    async (query) => {
      if (!enabled) {
        return [];
      }

      const trimmed = query.trim();
      if (trimmed.length < minimumQueryLength) {
        setOptions([]);
        setLoading(false);
        return [];
      }

      const requestId = pendingRequestId.current + 1;
      pendingRequestId.current = requestId;
      setLoading(true);
      setError('');

      try {
        const params = new URLSearchParams();
        params.set('search', trimmed);
        if (companyId !== null && companyId !== undefined && companyId !== '') {
          params.set('companyId', companyId);
        }

        const response = await apiRequest(`/api/admin/settings/outlets?${params.toString()}`, {}, navigate);
        const rows = Array.isArray(response.data) ? response.data : [];
        const mapped = rows
          .filter((outlet) => outlet?.id != null)
          .map((outlet) => mapOutletToOption(outlet));

        if (pendingRequestId.current === requestId) {
          setOptions(mapped);
        }

        return mapped;
      } catch (err) {
        if (pendingRequestId.current === requestId) {
          setOptions([]);
          setError(err.message || 'Failed to search outlets');
        }
        return [];
      } finally {
        if (pendingRequestId.current === requestId) {
          setLoading(false);
        }
      }
    },
    [companyId, enabled, minimumQueryLength, navigate],
  );

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (!enabled) {
      pendingRequestId.current += 1;
      setOptions([]);
      setError('');
      setLoading(false);
      return undefined;
    }

    if (!searchTerm || searchTerm.trim().length < minimumQueryLength) {
      setOptions([]);
      setError('');
      setLoading(false);
      return undefined;
    }

    debounceRef.current = setTimeout(() => {
      search(searchTerm);
    }, 300);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [enabled, minimumQueryLength, search, searchTerm]);

  const clearOptions = useCallback(() => {
    setOptions([]);
  }, []);

  return {
    searchTerm,
    setSearchTerm,
    options,
    loading,
    error,
    search,
    clearOptions,
  };
};

export default useOutletSearch;
